import { useForm } from "@/core/hooks";

import { createTaskSchema, type CreateTaskFormData } from "@/tasks/form/schema";

interface CreateFormProps {
  onSubmit: (data: CreateTaskFormData) => void;
  isLoading?: boolean;
}

const CreateForm = ({ onSubmit, isLoading = false }: CreateFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateTaskFormData>(createTaskSchema, {
    defaultValues: {
      title: "",
      userId: 1,
      completed: false,
    },
  });

  const handleFormSubmit = (data: CreateTaskFormData) => {
    onSubmit(data);
    reset();
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div>
        <label
          htmlFor="title"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Título da Tarefa
        </label>
        <input
          {...register("title")}
          type="text"
          id="title"
          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
            errors.title ? "border-red-300 bg-red-50" : "border-gray-300"
          }`}
          placeholder="Digite o título da tarefa..."
          disabled={isLoading || isSubmitting}
        />
        {errors.title && (
          <p className="mt-2 text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="userId"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          ID do Usuário
        </label>
        <select
          {...register("userId", { valueAsNumber: true })}
          id="userId"
          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
            errors.userId ? "border-red-300 bg-red-50" : "border-gray-300"
          }`}
          disabled={isLoading || isSubmitting}
        >
          {Array.from({ length: 10 }, (_, i) => i + 1).map((id) => (
            <option key={id} value={id}>
              Usuário {id}
            </option>
          ))}
        </select>
        {errors.userId && (
          <p className="mt-2 text-sm text-red-600">{errors.userId.message}</p>
        )}
      </div>

      <div className="flex items-center">
        <input
          {...register("completed")}
          type="checkbox"
          id="completed"
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          disabled={isLoading || isSubmitting}
        />
        <label htmlFor="completed" className="ml-2 text-sm text-gray-700">
          Marcar como concluída
        </label>
      </div>

      <div className="flex gap-4">
        <button
          type="submit"
          disabled={isLoading || isSubmitting}
          className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading || isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Criando...
            </div>
          ) : (
            "Criar Tarefa"
          )}
        </button>

        <button
          type="button"
          onClick={() => reset()}
          disabled={isLoading || isSubmitting}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Limpar
        </button>
      </div>
    </form>
  );
};

export { CreateForm };
